import type { DiagnosticTabKey, UsageTabKey } from '../../types';
import { UsageTab } from '../../types';
import { usageTabConfig } from '../constants/usageTabConfig';

import type { LimitNameInfo } from './getLimitDisplayName';
import { getLimitDisplayName, getLimitSummaryTooltip } from './getLimitDisplayName';

export function getUsageSummaryTooltip(tab: UsageTab): string | undefined {
  if (tab === UsageTab.OVERVIEW) {
    return undefined;
  }
  const override = TAB_NAME_OVERRIDES[tab];
  if (override) {
    return override.summaryTooltip;
  }
  // Limit-based tabs (e.g. Streaming / Warehouse data export) carry their tooltip on the
  // limit info; diagnostic tabs (e.g. Metrics) carry it on DIAGNOSTIC_NAME_INFO.
  const limitName = usageTabConfig[tab]?.limitName;
  if (limitName) {
    return getLimitSummaryTooltip(limitName);
  }
  return DIAGNOSTIC_NAME_INFO[tab as DiagnosticTabKey]?.summaryTooltip;
}

export function getUsageDisplayName(tab: UsageTab, display: keyof LimitNameInfo = 'titleName'): string {
  if (tab === UsageTab.OVERVIEW) {
    return 'Overview';
  }

  const override = TAB_NAME_OVERRIDES[tab];
  if (override) {
    return override[display] ?? override.titleName;
  }

  const limitName = usageTabConfig[tab].limitName;
  if (limitName) {
    return getLimitDisplayName(limitName, display);
  }

  const diagnosticTab = tab as DiagnosticTabKey;

  if (display === 'longTitleName') {
    return DIAGNOSTIC_NAME_INFO[diagnosticTab].longTitleName ?? DIAGNOSTIC_NAME_INFO[diagnosticTab].titleName;
  }
  return DIAGNOSTIC_NAME_INFO[diagnosticTab][display] ?? DIAGNOSTIC_NAME_INFO[diagnosticTab].titleName;
}

const TAB_NAME_OVERRIDES: Partial<Record<UsageTabKey, LimitNameInfo>> = {
  [UsageTab.DATA_EXPORT_COMBINED]: {
    titleName: 'Data Export',
    longTitleName: 'Data Export transfers',
    summaryTooltip:
      'Data Export limits are measured in transfers to a streaming destination or warehouse. Each streamed event or exported row counts as 1 transfer.',
    displayName: 'data export transfer',
    plural: 'data export transfers',
    abbreviated: 'transfer',
  },
};

export const DIAGNOSTIC_NAME_INFO: Record<DiagnosticTabKey, LimitNameInfo> = {
  [UsageTab.TOTAL_CONTEXTS]: {
    titleName: 'Total contexts',
    displayName: 'total context',
    plural: 'total contexts',
    abbreviated: 'context',
  },
  [UsageTab.CLIENT_SIDE_CONTEXTS]: {
    titleName: 'Client-side contexts',
    displayName: 'client-side context',
    plural: 'client-side contexts',
    abbreviated: 'context',
  },
  [UsageTab.SERVER_SIDE_CONTEXTS]: {
    titleName: 'Server-side contexts',
    displayName: 'server-side context',
    plural: 'server-side contexts',
    abbreviated: 'context',
  },
  [UsageTab.METRICS]: {
    titleName: 'Metrics',
    longTitleName: 'Observability metrics',
    summaryTooltip: 'Month-to-date total of hourly unique metrics divided by total hours in the month',
    displayName: 'metric series',
    plural: 'metric series',
    abbreviated: 'series',
  },
  [UsageTab.VEGA_AI]: {
    titleName: 'Vega',
    longTitleName: 'Vega',
    displayName: 'Vega prompt',
    plural: 'Vega prompts',
    abbreviated: 'prompt',
  },
  [UsageTab.CLIENT_CONNECTIONS]: {
    titleName: 'Client connections',
    displayName: 'client connection',
    plural: 'client connections',
    abbreviated: 'connection',
  },
  [UsageTab.PEAK_CONCURRENT_CONNECTIONS]: {
    titleName: 'Peak connections',
    longTitleName: 'Peak concurrent connections from server-side SDKs',
    displayName: 'peak connection',
    plural: 'peak connections',
    abbreviated: 'connection',
  },
  [UsageTab.WAREHOUSE_DATA_EXPORT]: {
    titleName: 'Warehouse data export',
    displayName: 'warehouse data export',
    plural: 'warehouse data export rows',
    abbreviated: 'row',
    summaryTooltip:
      'Rows delivered to data warehouse destinations like Snowflake, BigQuery, Databricks, Redshift, and ClickHouse',
  },
};
